import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { motion } from "framer-motion";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from "recharts";
import { 
  Beaker, FlaskConical, TestTube2, CheckCircle2, Clock, 
  TrendingUp, Users, Activity, RefreshCw, ArrowUpRight, Package
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import LabAssistantDashboard from "./LabAssistantDashboard";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

// Premium Glass Card Component - Light Theme
const GlassCard = ({ children, className = "", ...props }) => (
  <motion.div 
    variants={itemVariants}
    className={`relative backdrop-blur-xl bg-white/80 border border-vcc-pink-100 rounded-2xl shadow-lg shadow-vcc-pink-100/20 ${className}`}
    whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(244, 114, 182, 0.15)" }}
    transition={{ duration: 0.2 }}
    {...props}
  >
    <div className="relative z-10">{children}</div>
  </motion.div>
);

// Stat Card Component - Light Theme
const StatCard = ({ icon: Icon, label, value, trend, color, bgColor, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
    className="relative group"
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className={`absolute inset-0 ${bgColor} rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
    <div className="relative backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-6 overflow-hidden shadow-lg">
      {/* Decorative gradient */}
      <div className={`absolute top-0 right-0 w-32 h-32 ${bgColor} opacity-20 blur-3xl`} />
      
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-500 text-sm font-poppins uppercase tracking-wider">{label}</p>
          <p className="text-4xl font-poppins font-bold text-slate-800 mt-2">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <ArrowUpRight size={14} className="text-emerald-500" />
              <span className="text-emerald-500 text-sm font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div className={`w-14 h-14 rounded-xl ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${color}`} />
        </div>
      </div>
    </div>
  </motion.div>
);

// Chart tooltip - Light Theme
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="backdrop-blur-xl bg-white/95 border border-vcc-pink-100 rounded-lg p-3 shadow-lg">
        <p className="text-slate-800 font-poppins font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function NewDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [recentProducts, setRecentProducts] = useState([]);
  
  const isLabAssistant = user?.role === "Lab Assistant";

  const fetchData = useCallback(async () => {
    if (isLabAssistant) {
      setLoading(false);
      return; // Skip fetch for lab assistants as they use a different dashboard
    }
    try {
      const [statsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/dashboard/product-stats`),
        axios.get(`${API}/product-folders`, { params: { limit: 5 } })
      ]);
      
      if (statsRes.data.success) {
        setStats(statsRes.data.stats || {});
      }
      if (foldersRes.data.success) {
        setRecentProducts(foldersRes.data.folders?.slice(0, 5) || []);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [isLabAssistant]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // If user is Lab Assistant, render the specialized dashboard
  if (isLabAssistant) {
    return <LabAssistantDashboard />;
  }

  const handleSyncFromZoho = async () => {
    setSyncing(true);
    try {
      // Step 1: Sync product folders from R&D
      const response = await axios.post(`${API}/product-folders/sync-from-rd`);
      if (response.data.success) {
        // Step 2: Full sync iterations from NPD records (authoritative sync)
        await axios.post(`${API}/zoho/full-sync-iterations`);
        fetchData();
      }
    } catch (error) {
      console.error("Sync failed:", error);
    } finally {
      setSyncing(false);
    }
  };

  // Chart data - mapped to Job_Stage from Zoho Client Requirements
  // New -> Not Started, Under R&D -> In Progress, Sample Ready/Sent/Waiting/Feedback -> Testing, Approved -> Completed
  const statusData = [
    { name: "New", value: stats.notStarted || 0, color: "#94A3B8" },
    { name: "Under R&D", value: stats.inProgress || 0, color: "#38BDF8" },
    { name: "Sample Testing", value: stats.testing || 0, color: "#FBBF24" },
    { name: "Approved", value: stats.completed || 0, color: "#10B981" },
  ];

  const monthlyData = [
    { month: "Jan", products: 12 },
    { month: "Feb", products: 19 },
    { month: "Mar", products: 15 },
    { month: "Apr", products: 25 },
    { month: "May", products: 22 },
    { month: "Jun", products: stats.total || 30 },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <RefreshCw className="w-8 h-8 text-vcc-teal" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" data-testid="dashboard-page">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1 className="text-3xl font-poppins font-bold text-slate-800">
            Welcome back, <span className="text-vcc-teal">{user?.name?.split(' ')[0] || 'User'}</span>
          </h1>
          <p className="text-slate-500 mt-1 font-poppins">Here's what's happening with your products today</p>
        </div>
        
        <motion.button
          onClick={handleSyncFromZoho}
          disabled={syncing}
          className="flex items-center gap-2 px-5 py-2.5 bg-vcc-teal text-white rounded-xl font-poppins font-medium shadow-lg shadow-vcc-teal/20 hover:shadow-vcc-teal/40 transition-all disabled:opacity-50"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="sync-btn"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync from Zoho'}
        </motion.button>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <StatCard 
          icon={Package} 
          label="Total Requirements" 
          value={stats.total || 0}
          trend="+12% this month"
          color="text-vcc-teal"
          bgColor="bg-vcc-teal/20"
          delay={0}
        />
        <StatCard 
          icon={Activity} 
          label="Under R&D" 
          value={stats.inProgress || 0}
          color="text-blue-500"
          bgColor="bg-blue-100"
          delay={0.1}
        />
        <StatCard 
          icon={FlaskConical} 
          label="Sample Testing" 
          value={stats.testing || 0}
          color="text-amber-500"
          bgColor="bg-amber-100"
          delay={0.2}
        />
        <StatCard 
          icon={CheckCircle2} 
          label="Approved" 
          value={stats.completed || 0}
          color="text-emerald-500"
          bgColor="bg-emerald-100"
          delay={0.3}
        />
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Monthly Trend Chart */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-poppins font-semibold text-slate-800 mb-4">Monthly Product Trend</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="colorProducts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0D9488" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0D9488" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="products" 
                  stroke="#0D9488" 
                  fillOpacity={1} 
                  fill="url(#colorProducts)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Status Distribution */}
        <GlassCard className="p-6">
          <h3 className="text-lg font-poppins font-semibold text-slate-800 mb-4">Status Distribution</h3>
          <div style={{ width: '100%', height: 256 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {statusData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="text-sm text-slate-600 font-poppins">{item.name}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Recent Products */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-poppins font-semibold text-slate-800">Recent Products</h3>
          <motion.button
            onClick={() => navigate('/products')}
            className="text-vcc-teal font-poppins font-medium text-sm hover:underline flex items-center gap-1"
            whileHover={{ x: 5 }}
          >
            View All <ArrowUpRight size={14} />
          </motion.button>
        </div>
        
        <div className="space-y-3">
          {recentProducts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-poppins">No products yet. Sync from Zoho to get started.</p>
            </div>
          ) : (
            recentProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center justify-between p-4 rounded-xl bg-vcc-cream-50/50 hover:bg-vcc-pink-50/50 transition-colors cursor-pointer border border-transparent hover:border-vcc-pink-100"
                onClick={() => navigate(`/products/${product.id}`)}
                data-testid={`product-${product.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-vcc-teal/10 flex items-center justify-center">
                    <Beaker className="w-5 h-5 text-vcc-teal" />
                  </div>
                  <div>
                    <p className="font-poppins font-medium text-slate-800">{product.productName}</p>
                    <p className="text-sm text-slate-500">Sample: {product.sampleNo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    product.testStatus === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                    product.testStatus === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {product.testStatus || 'Not Started'}
                  </span>
                  <ArrowUpRight size={16} className="text-slate-400" />
                </div>
              </motion.div>
            ))
          )}
        </div>
      </GlassCard>
    </div>
  );
}
